import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * /privacy — PlayFirst Privacy Policy.
 *
 * Rewritten Feb 2026 per legal positioning requirements. Long-form, plain
 * language, professional + legally cautious. This file is the verbatim
 * source of truth — wording must not be casually edited without legal
 * review.
 */
export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="rounded-full mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-700" />
            </div>
            <div>
              <h1 className="text-3xl font-serif text-primary-900">Privacy Policy</h1>
              <p className="text-sm text-stone-500">Last updated: 25 May 2026</p>
            </div>
          </div>

          <article className="prose prose-stone max-w-none text-stone-700 leading-relaxed space-y-6">

            <h2 className="text-xl font-serif text-primary-900 mt-8">1. Introduction</h2>
            <p>
              PlayFirst is a digital platform designed for speech-language and allied health professionals.
              PlayFirst provides tools for therapy planning, documentation support, AI-assisted drafting,
              parent collaboration, activity generation, progress tracking, and related workflow support.
            </p>
            <p>This Privacy Policy explains how PlayFirst collects, uses, stores, shares, transfers, protects, and deletes personal information.</p>
            <p>This Privacy Policy applies to:</p>
            <ul className="list-disc pl-6">
              <li>PlayFirst Full</li>
              <li>PlayFirst Lite</li>
              <li>The PlayFirst website</li>
              <li>Parent/caregiver portal functionality</li>
              <li>AI-assisted features</li>
              <li>Subscription and payment systems</li>
              <li>Support and communication services</li>
            </ul>
            <p>By using PlayFirst, you acknowledge that you have read and understood this Privacy Policy.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">2. Product Distinction: PlayFirst Full vs PlayFirst Lite</h2>

            <h3 className="text-lg font-medium text-primary-900 mt-4">PlayFirst Full</h3>
            <p>PlayFirst Full is a clinical documentation and workflow platform that may store:</p>
            <ul className="list-disc pl-6">
              <li>therapist account information;</li>
              <li>child/client profiles;</li>
              <li>therapy notes;</li>
              <li>assessment information;</li>
              <li>generated reports;</li>
              <li>generated activities;</li>
              <li>parent portal content; and</li>
              <li>related workflow information.</li>
            </ul>
            <p>PlayFirst Full is designed for responsible clinical data handling and is built around the core privacy requirements of:</p>
            <ul className="list-disc pl-6">
              <li>South Africa's Protection of Personal Information Act (POPIA);</li>
              <li>the Australian Privacy Principles (APPs);</li>
              <li>the New Zealand Privacy Act 2020;</li>
              <li>relevant GDPR and UK GDPR principles where applicable.</li>
            </ul>
            <p>PlayFirst Full is intended for professional use by therapy professionals in:</p>
            <ul className="list-disc pl-6">
              <li>South Africa;</li>
              <li>Australia;</li>
              <li>New Zealand;</li>
              <li>the United States;</li>
              <li>the United Kingdom; and</li>
              <li>Ireland.</li>
            </ul>
            <p>
              PlayFirst Full is designed with HIPAA-aware privacy and security principles in mind and incorporates
              safeguards commonly associated with privacy-conscious healthcare software, including consent confirmation
              workflows, audit logging, secure authentication, access controls, secure infrastructure, and data
              minimisation practices. However, PlayFirst Full is not currently marketed as HIPAA compliant and does
              not currently operate under a Business Associate Agreement (BAA) chain.
            </p>

            <h3 className="text-lg font-medium text-primary-900 mt-4">PlayFirst Lite</h3>
            <p>PlayFirst Lite is a separate stateless AI workspace designed around:</p>
            <ul className="list-disc pl-6">
              <li>de-identification;</li>
              <li>data minimisation; and</li>
              <li>temporary processing.</li>
            </ul>
            <p>PlayFirst Lite is designed so that clinical inputs and AI outputs are not intentionally stored in a PlayFirst-controlled database.</p>
            <p>PlayFirst Lite is intended for de-identified use only.</p>
            <p>Users must not submit:</p>
            <ul className="list-disc pl-6">
              <li>Protected Health Information (PHI);</li>
              <li>identifiable client information; or</li>
              <li>unnecessary identifying personal information</li>
            </ul>
            <p>into PlayFirst Lite.</p>
            <p>PlayFirst Lite is designed around HIPAA Safe Harbor de-identification principles, but it is not marketed as HIPAA compliant.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">3. Who We Are</h2>
            <p>PlayFirst is operated by:</p>
            <p>
              <strong>PlayFirst</strong><br />
              Email: <a href="mailto:michaela@playfirstapp.com" className="text-primary underline">michaela@playfirstapp.com</a><br />
              Website: <a href="https://www.playfirstapp.com" className="text-primary underline" target="_blank" rel="noopener noreferrer">https://www.playfirstapp.com</a>
            </p>
            <p>For privacy-related requests or concerns, contact:</p>
            <p>
              <strong>Privacy / Information Officer:</strong>{' '}
              <a href="mailto:michaela@playfirstapp.com" className="text-primary underline">michaela@playfirstapp.com</a>
            </p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">4. Important Privacy Principles</h2>
            <p>PlayFirst is designed around the following principles:</p>
            <ul className="list-disc pl-6">
              <li>collect only the minimum information reasonably necessary;</li>
              <li>encourage therapists to minimise identifying information;</li>
              <li>provide user-controlled access;</li>
              <li>provide deletion and export functionality;</li>
              <li>maintain secure infrastructure and authentication controls;</li>
              <li>maintain transparency regarding third-party processing;</li>
              <li>support responsible clinical data handling.</li>
            </ul>
            <p>PlayFirst does not sell personal or client data.</p>
            <p>PlayFirst only shares data with trusted service providers where necessary to:</p>
            <ul className="list-disc pl-6">
              <li>operate the platform;</li>
              <li>secure the platform;</li>
              <li>process payments;</li>
              <li>deliver emails;</li>
              <li>provide AI functionality; or</li>
              <li>support core infrastructure.</li>
            </ul>

            <h2 className="text-xl font-serif text-primary-900 mt-8">5. Information We Collect</h2>

            <h3 className="text-lg font-medium text-primary-900 mt-4">5.1 Therapist Account Information</h3>
            <p>We may collect:</p>
            <ul className="list-disc pl-6">
              <li>name;</li>
              <li>email address;</li>
              <li>practice or clinic name;</li>
              <li>country;</li>
              <li>subscription details;</li>
              <li>billing information;</li>
              <li>authentication information;</li>
              <li>optional professional registration details.</li>
            </ul>

            <h3 className="text-lg font-medium text-primary-900 mt-4">5.2 Child / Client Information (PlayFirst Full)</h3>
            <p>Therapists may enter:</p>
            <ul className="list-disc pl-6">
              <li>first name;</li>
              <li>initials;</li>
              <li>nickname;</li>
              <li>age or age range;</li>
              <li>interests;</li>
              <li>strengths;</li>
              <li>communication style;</li>
              <li>therapy goals;</li>
              <li>assessment observations;</li>
              <li>progress notes;</li>
              <li>generated reports;</li>
              <li>generated activities;</li>
              <li>language samples;</li>
              <li>parent portal information;</li>
              <li>parent/caregiver contact details where added by the therapist.</li>
            </ul>
            <p>PlayFirst strongly encourages therapists to use only the minimum information necessary.</p>

            <h3 className="text-lg font-medium text-primary-900 mt-4">5.3 Session Notes and Reports</h3>
            <p>Therapists may create or store:</p>
            <ul className="list-disc pl-6">
              <li>session notes;</li>
              <li>SOAP notes;</li>
              <li>assessments;</li>
              <li>recommendations;</li>
              <li>reports;</li>
              <li>generated therapy activities;</li>
              <li>parent summaries;</li>
              <li>AI-assisted outputs.</li>
            </ul>

            <h3 className="text-lg font-medium text-primary-900 mt-4">5.4 Technical and Usage Information</h3>
            <p>We may collect:</p>
            <ul className="list-disc pl-6">
              <li>login timestamps;</li>
              <li>IP addresses or hashed IPs;</li>
              <li>browser and device information;</li>
              <li>security logs;</li>
              <li>failed login attempts;</li>
              <li>audit logs;</li>
              <li>usage metrics;</li>
              <li>subscription status;</li>
              <li>payment references.</li>
            </ul>

            <h3 className="text-lg font-medium text-primary-900 mt-4">5.5 Payment Information</h3>
            <p>Payments are processed through third-party payment providers.</p>
            <p>PlayFirst does not store full payment card details.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">6. Information We Do Not Intentionally Collect</h2>
            <p>PlayFirst discourages users from entering:</p>
            <ul className="list-disc pl-6">
              <li>unnecessary identifying information;</li>
              <li>full names where not needed;</li>
              <li>addresses;</li>
              <li>ID numbers;</li>
              <li>medical aid numbers;</li>
              <li>social security numbers;</li>
              <li>unnecessary family details;</li>
              <li>unnecessary sensitive personal information.</li>
            </ul>
            <p>PlayFirst Lite users must not submit:</p>
            <ul className="list-disc pl-6">
              <li>PHI;</li>
              <li>identifiable client information; or</li>
              <li>unnecessary identifying information.</li>
            </ul>

            <h2 className="text-xl font-serif text-primary-900 mt-8">7. Data Minimisation Guidance</h2>
            <p>PlayFirst includes in-app guidance encouraging therapists to:</p>
            <ul className="list-disc pl-6">
              <li>use first names, initials, or non-identifying nicknames where possible;</li>
              <li>avoid unnecessary identifying details;</li>
              <li>focus notes on therapy observations and goals;</li>
              <li>avoid storing unnecessary family or personal information.</li>
            </ul>
            <p>Suggested best practices include avoiding:</p>
            <ul className="list-disc pl-6">
              <li>surnames;</li>
              <li>addresses;</li>
              <li>school names;</li>
              <li>ID numbers;</li>
              <li>medical aid numbers;</li>
              <li>unnecessary parent contact information.</li>
            </ul>
            <p>Therapists remain responsible for deciding what information is entered into the platform.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">8. Why We Process Information</h2>
            <p>We process information:</p>
            <ul className="list-disc pl-6">
              <li>to provide the PlayFirst service selected by the therapist;</li>
              <li>to support therapy documentation and workflow functionality;</li>
              <li>to provide AI-assisted drafting and generation tools;</li>
              <li>to manage accounts and subscriptions;</li>
              <li>to provide parent portal functionality;</li>
              <li>to provide customer support;</li>
              <li>to maintain platform security and integrity;</li>
              <li>to investigate suspicious activity or security incidents;</li>
              <li>to comply with legal, operational and regulatory obligations.</li>
            </ul>

            <h2 className="text-xl font-serif text-primary-900 mt-8">9. Consent and Therapist Responsibilities</h2>
            <p>Therapists are responsible for:</p>
            <ul className="list-disc pl-6">
              <li>obtaining appropriate consent or authority from the client, parent or legal guardian;</li>
              <li>ensuring that information entered into PlayFirst is lawful and appropriate;</li>
              <li>complying with applicable privacy laws and professional obligations;</li>
              <li>maintaining their own clinical and consent records.</li>
            </ul>
            <p>PlayFirst does not obtain client or guardian consent directly on behalf of therapists.</p>
            <p>Before creating child/client profiles, therapists are required to confirm:</p>
            <blockquote className="border-l-4 border-primary/40 pl-4 italic text-stone-600">
              "I confirm that I have obtained appropriate consent or authority from the client, parent or legal guardian to create this profile and process this client's information in PlayFirst."
            </blockquote>
            <p>PlayFirst may store:</p>
            <ul className="list-disc pl-6">
              <li>consent confirmation;</li>
              <li>timestamp;</li>
              <li>consent version;</li>
              <li>therapist account reference;</li>
              <li>related audit log entries.</li>
            </ul>

            <h2 className="text-xl font-serif text-primary-900 mt-8">10. AI Features and AI Providers</h2>
            <p>PlayFirst uses artificial intelligence to assist with:</p>
            <ul className="list-disc pl-6">
              <li>report generation;</li>
              <li>parent summaries;</li>
              <li>therapy activity generation;</li>
              <li>GLP support;</li>
              <li>documentation support;</li>
              <li>workflow drafting.</li>
            </ul>
            <p>AI outputs:</p>
            <ul className="list-disc pl-6">
              <li>may contain inaccuracies;</li>
              <li>may contain omissions;</li>
              <li>may not always be clinically appropriate;</li>
              <li>must be professionally reviewed before use.</li>
            </ul>
            <p>Therapists remain responsible for all final clinical decisions and documents.</p>

            <h3 className="text-lg font-medium text-primary-900 mt-4">AI Providers</h3>
            <p>PlayFirst may use third-party AI providers through Emergent's LLM proxy, including:</p>
            <ul className="list-disc pl-6">
              <li>OpenAI;</li>
              <li>Anthropic.</li>
            </ul>
            <p>
              Emergent has confirmed that its LLM proxy acts as a pass-through and does not log or retain prompt/output
              content.
            </p>
            <p>However:</p>
            <ul className="list-disc pl-6">
              <li>zero data retention is not currently enabled for the pooled LLM key used through Emergent;</li>
              <li>AI providers may temporarily retain API data for abuse monitoring, security or legal purposes under their own terms;</li>
              <li>AI providers state that API data is not used to train their models by default unless users opt in or provider terms state otherwise.</li>
            </ul>
            <p>Users should avoid entering unnecessary identifying information into AI tools.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">11. Parent / Caregiver Portal</h2>
            <p>Parent portal access is controlled by the therapist.</p>
            <p>Therapists decide:</p>
            <ul className="list-disc pl-6">
              <li>whether to invite a parent/caregiver;</li>
              <li>what information is shared;</li>
              <li>which notes or activities are visible.</li>
            </ul>
            <p>Parents/caregivers may access:</p>
            <ul className="list-disc pl-6">
              <li>shared activities;</li>
              <li>shared goals;</li>
              <li>shared notes;</li>
              <li>therapist-approved content.</li>
            </ul>
            <p>Parents/caregivers do not automatically receive access to:</p>
            <ul className="list-disc pl-6">
              <li>all clinical notes;</li>
              <li>all reports;</li>
              <li>all assessment data.</li>
            </ul>
            <p>Therapists are responsible for ensuring that they have appropriate consent before sharing information through the parent portal.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">12. International Processing and Data Residency</h2>
            <p>
              PlayFirst uses cloud-based infrastructure and service providers that may process or store data outside
              the user's country, including in the United States.
            </p>
            <p>This may include:</p>
            <ul className="list-disc pl-6">
              <li>hosting infrastructure;</li>
              <li>database hosting;</li>
              <li>AI processing;</li>
              <li>analytics;</li>
              <li>payment processing;</li>
              <li>email delivery;</li>
              <li>security tooling.</li>
            </ul>
            <p>
              PlayFirst currently uses Emergent-managed infrastructure with default data residency in the United States.
            </p>
            <p>PlayFirst does not currently guarantee region-specific hosting or dedicated EU-region infrastructure.</p>
            <p>Users are responsible for ensuring that their use of PlayFirst is consistent with their own professional and legal obligations.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">13. Third-Party Service Providers and Subprocessors</h2>
            <p>PlayFirst may use third-party service providers including:</p>
            <ul className="list-disc pl-6">
              <li>Emergent.sh;</li>
              <li>OpenAI;</li>
              <li>Anthropic;</li>
              <li>MongoDB Atlas;</li>
              <li>Cloudflare;</li>
              <li>Paystack;</li>
              <li>Resend;</li>
              <li>analytics providers;</li>
              <li>security providers.</li>
            </ul>
            <p>These providers may process limited information where necessary to operate the service.</p>
            <p>PlayFirst maintains a subprocessor register and vendor documentation where available.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">14. Security Measures</h2>
            <p>PlayFirst applies reasonable technical and organisational safeguards including:</p>
            <ul className="list-disc pl-6">
              <li>HTTPS/TLS encryption;</li>
              <li>encrypted databases;</li>
              <li>hashed passwords;</li>
              <li>access controls;</li>
              <li>restricted administrative access;</li>
              <li>audit logging;</li>
              <li>login protection measures;</li>
              <li>backup systems;</li>
              <li>monitoring and security tooling.</li>
            </ul>
            <p>However:</p>
            <ul className="list-disc pl-6">
              <li>no online system can be guaranteed completely secure;</li>
              <li>users remain responsible for maintaining secure passwords and device security.</li>
            </ul>

            <h2 className="text-xl font-serif text-primary-900 mt-8">15. Audit Logging and Monitoring</h2>
            <p>PlayFirst may maintain audit logs relating to:</p>
            <ul className="list-disc pl-6">
              <li>login events;</li>
              <li>failed login attempts;</li>
              <li>profile creation;</li>
              <li>deletion requests;</li>
              <li>exports;</li>
              <li>parent portal access;</li>
              <li>administrative actions;</li>
              <li>security events.</li>
            </ul>
            <p>Audit logs are maintained for security, compliance and operational purposes.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">16. Retention and Deletion</h2>
            <p>PlayFirst retains information:</p>
            <ul className="list-disc pl-6">
              <li>while accounts remain active;</li>
              <li>while profiles remain active;</li>
              <li>while required for operational, security or legal purposes.</li>
            </ul>
            <p>Users may:</p>
            <ul className="list-disc pl-6">
              <li>delete child/client profiles;</li>
              <li>delete notes;</li>
              <li>export data;</li>
              <li>request account deletion.</li>
            </ul>
            <p>Deleted data is removed from active systems.</p>
            <p>Deleted data may remain temporarily in secure backups until routine backup expiry.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">17. Your Rights</h2>
            <p>Depending on your country and applicable law, users may have rights including:</p>
            <ul className="list-disc pl-6">
              <li>access;</li>
              <li>correction;</li>
              <li>deletion;</li>
              <li>export/portability;</li>
              <li>objection or withdrawal of consent where applicable.</li>
            </ul>
            <p>Users may contact PlayFirst regarding privacy requests.</p>
            <p>Some information may be retained where required for:</p>
            <ul className="list-disc pl-6">
              <li>legal purposes;</li>
              <li>security purposes;</li>
              <li>fraud prevention;</li>
              <li>backup systems;</li>
              <li>operational integrity.</li>
            </ul>

            <h2 className="text-xl font-serif text-primary-900 mt-8">18. Regional Privacy Positioning</h2>

            <h3 className="text-lg font-medium text-primary-900 mt-4">South Africa</h3>
            <p>PlayFirst is designed around the core privacy principles of POPIA and supports privacy-conscious handling of personal information by therapy professionals.</p>

            <h3 className="text-lg font-medium text-primary-900 mt-4">Australia</h3>
            <p>PlayFirst is designed around the core principles of the Australian Privacy Principles and supports responsible handling of therapy-related information.</p>

            <h3 className="text-lg font-medium text-primary-900 mt-4">New Zealand</h3>
            <p>PlayFirst is designed around the core principles of the New Zealand Privacy Act 2020 and supports privacy-conscious handling of therapy-related information.</p>

            <h3 className="text-lg font-medium text-primary-900 mt-4">United States</h3>
            <p>PlayFirst Full is not marketed as HIPAA compliant and does not currently operate under a BAA chain.</p>
            <p>PlayFirst Lite is intended for de-identified use only.</p>

            <h3 className="text-lg font-medium text-primary-900 mt-4">United Kingdom and Ireland</h3>
            <p>PlayFirst applies transparency, data minimisation, access control and security practices designed around GDPR-style privacy principles where applicable.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">19. Privacy Breaches and Security Incidents</h2>
            <p>PlayFirst maintains an internal breach response process.</p>
            <p>Where required by applicable law, PlayFirst may:</p>
            <ul className="list-disc pl-6">
              <li>investigate suspected incidents;</li>
              <li>notify affected users;</li>
              <li>notify relevant regulators.</li>
            </ul>

            <h2 className="text-xl font-serif text-primary-900 mt-8">20. Changes to This Privacy Policy</h2>
            <p>PlayFirst may update this Privacy Policy from time to time.</p>
            <p>Updated versions will be posted on the website or inside the platform.</p>
            <p>Continued use of the platform after changes are published may constitute acceptance of the updated policy.</p>

            <h2 className="text-xl font-serif text-primary-900 mt-8">21. Contact</h2>
            <p>
              For legal or privacy-related matters contact:{' '}
              <a href="mailto:michaela@playfirstapp.com" className="text-primary underline">michaela@playfirstapp.com</a>
            </p>
            <p>
              Website:{' '}
              <a href="https://www.playfirstapp.com" className="text-primary underline" target="_blank" rel="noopener noreferrer">https://www.playfirstapp.com</a>
            </p>

          </article>
        </motion.div>
      </div>
    </div>
  );
}
